import React, { useEffect } from 'react';
import { toFlag } from 'pano-utils';
import { useImmer } from 'use-immer';
import {
  type EnterCover,
  EnterCoverEnter,
  EnterCoverPosition,
  EnterCoverType,
  ImageType,
} from '@types';
import addComponent from '@utils/addComponent';
import { Collapse } from '@components/Collapse';
import ImageContainer from '@components/ImageContainer';
import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import Image from '@components/Image';
import { Dropdown } from '@components/Dropdown';
import Slider from '@components/Slider';
import ToggleButton from '@components/ToggleButton';
import ColorPicker from '@components/ColorPicker';
import Tip from '@components/Tip';
import Title from '@components/Title';
import useTourEditing from '@hooks/useTourEditing';
import getVideoDetail from '@utils/getVideoDetail';
import TextLink from '@components/TextLink';
import VideoCaptureModal from '@components/VideoCaptureModal';
import { getMediaUrl } from '@utils/urlMakers';
import uploadVideoCover from '@utils/uploadVideoCover';
import FeatureToggle from '@components/FeatureToggle';
import { $editingEnterCover } from '@stores/editingStores';
import { selectLibImages, selectLibVideos } from '../../mediaLib';
import PanelBase from '../PanelBase';
import type { PanelBaseProps } from '../types';

type TypeKey = 'pc' | 'mobile';

const getKeys = (typeKey: TypeKey) => {
  const idKey = `${typeKey}CoverId` as const;
  const urlKey = `${typeKey}CoverUrl` as const;
  const posKey = `${typeKey}ShowPosition` as const;
  const vNameKey = `${typeKey}CoverVideoName` as const;
  const vThumbKey = `${typeKey}CoverVideoThumb` as const;
  const vDurKey = `${typeKey}CoverVideoDuration` as const;
  return { idKey, urlKey, posKey, vNameKey, vThumbKey, vDurKey };
};
const keysMap = {
  pc: getKeys('pc'),
  mobile: getKeys('mobile'),
};

function EnterCoverBase({
  defaultValue,
  onConfirm,
}: PanelBaseProps<EnterCover>) {
  const { state, setState, isChanged, defaultState } = useTourEditing(
    defaultValue,
    {
      type: EnterCoverType.image,
      bgColor: '0,0,0.1',
      bgImageId: 0,
      bgImageUrl: '',
      pcCoverId: 0,
      pcCoverUrl: '',
      pcShowPosition: EnterCoverPosition.center,
      pcCoverVideoName: '',
      pcCoverVideoDuration: 0,
      pcCoverVideoThumb: '',
      mobileCoverId: 0,
      mobileCoverUrl: '',
      mobileShowPosition: EnterCoverPosition.center,
      mobileCoverVideoName: '',
      mobileCoverVideoDuration: 0,
      mobileCoverVideoThumb: '',
      enterMethod: EnterCoverEnter.auto,
      enterDuration: 5,
      showCountDown: 0,
      autoPlay: 1,
      loop: 0,
    }
  );

  useEffect(() => {
    $editingEnterCover.api.set(state);
  }, [state]);

  const [loading, setLoading] = useImmer<Record<TypeKey, boolean>>({
    pc: false,
    mobile: false,
  });

  const hasCover = !!state.pcCoverUrl || !!state.mobileCoverUrl;

  const handleConfirm = () => {
    if (hasCover) {
      onConfirm(state);
    } else {
      onConfirm({});
    }
  };

  const handleTypeChange = (typeId: EnterCoverType) => {
    setState(() => ({
      ...defaultState,
      type: typeId,
    }));
  };

  const handleImageSelect = (typeKey: TypeKey) => async () => {
    const keys = keysMap[typeKey];
    const { libItems } = await selectLibImages();
    if (!libItems) return;
    setState((draft) => {
      draft[keys.idKey] = libItems[0].id;
      draft[keys.urlKey] = libItems[0].url;
    });
  };

  const handleImageDelete = (typeKey: TypeKey) => () => {
    const keys = keysMap[typeKey];
    setState((draft) => {
      draft[keys.idKey] = defaultState[keys.idKey];
      draft[keys.urlKey] = defaultState[keys.urlKey];
      draft[keys.posKey] = defaultState[keys.posKey];
    });
  };

  const handleVideoSelect = (typeKey: TypeKey) => async () => {
    const keys = keysMap[typeKey];
    const { libItems } = await selectLibVideos();
    if (!libItems) return;
    setLoading((draft) => {
      draft[typeKey] = true;
    });
    const item = libItems[0];
    const { coverUrl, duration } = await getVideoDetail(item.url);
    setState((draft) => {
      draft[keys.idKey] = item.id;
      draft[keys.urlKey] = item.url;
      draft[keys.vNameKey] = item.name;
      draft[keys.vThumbKey] = coverUrl;
      draft[keys.vDurKey] = duration;
    });
    setLoading((draft) => {
      draft[typeKey] = false;
    });
  };

  const handleVideoDelete = (typeKey: TypeKey) => () => {
    const keys = keysMap[typeKey];
    setState((draft) => {
      draft[keys.idKey] = defaultState[keys.idKey];
      draft[keys.urlKey] = defaultState[keys.urlKey];
      draft[keys.posKey] = defaultState[keys.posKey];
      draft[keys.vNameKey] = defaultState[keys.vNameKey];
      draft[keys.vThumbKey] = defaultState[keys.vThumbKey];
      draft[keys.vDurKey] = defaultState[keys.vDurKey];
    });
  };

  const handleVCoverSetting = (typeKey: TypeKey) => () => {
    const keys = keysMap[typeKey];
    addComponent((remove) => (
      <VideoCaptureModal
        coverSrc={state[keys.vThumbKey]}
        videoUrl={getMediaUrl(state[keys.urlKey])}
        onRequestClose={remove}
        onConfirm={async (file) => {
          const url = await uploadVideoCover(file);
          setState((draft) => {
            draft[keys.vThumbKey] = url;
          });
        }}
      />
    ));
  };

  const renderPosSetting = (typeKey: TypeKey) => {
    const keys = keysMap[typeKey];
    const isFullscreen = state[keys.posKey] === EnterCoverPosition.fullscreen;
    return (
      <div className="mt-20 flex items-center">
        <div>显示位置：</div>
        <Checkbox
          className="ml-auto"
          label="居中"
          checked={!isFullscreen}
          onClick={() =>
            setState((draft) => {
              draft[keys.posKey] = EnterCoverPosition.center;
            })
          }
        />
        <Checkbox
          className="ml-15"
          label="全屏"
          checked={isFullscreen}
          onClick={() =>
            setState((draft) => {
              draft[keys.posKey] = EnterCoverPosition.fullscreen;
            })
          }
        />
      </div>
    );
  };

  const renderCoverSetting = (params: {
    title: string;
    size: string;
    typeKey: TypeKey;
  }) => {
    const { title, size, typeKey } = params;
    const keys = keysMap[typeKey];
    const coverUrl = state[keys.urlKey];
    return (
      <Collapse title={title}>
        <ImageContainer
          placeholder="请先选择一张图片"
          className="h-115"
          onDeleteClick={handleImageDelete(typeKey)}
        >
          {!!coverUrl && (
            <Image
              src={coverUrl}
              type={ImageType.media}
              className="max-h-full max-w-full"
            />
          )}
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>{`建议尺寸：${size}`}</Tip>
          <Button size="sm" onClick={handleImageSelect(typeKey)}>
            选择图片
          </Button>
        </div>
        {!!coverUrl && renderPosSetting(typeKey)}
      </Collapse>
    );
  };

  const renderVideoSetting = (params: { title: string; typeKey: TypeKey }) => {
    const { title, typeKey } = params;
    const keys = keysMap[typeKey];
    const coverUrl = state[keys.vThumbKey];

    return (
      <Collapse title={title}>
        <ImageContainer
          className="h-70"
          placeholder={
            loading[typeKey] ? (
              <span className="text-white">视频封面正在生成中…</span>
            ) : (
              '请选择视频'
            )
          }
        >
          {!!coverUrl && (
            <>
              <Image
                className="h-50 w-50"
                size={50}
                src={coverUrl}
                type={ImageType.media}
              />
              <div className="ml-10 flex flex-col items-start">
                <div className="mb-5 w-140 truncate text-white">
                  {state[keys.vNameKey]}
                </div>
                <div className="flex items-center">
                  <TextLink onClick={handleVCoverSetting(typeKey)}>
                    设置封面
                  </TextLink>
                  <TextLink
                    className="ml-15"
                    onClick={handleVideoDelete(typeKey)}
                  >
                    删除
                  </TextLink>
                </div>
              </div>
            </>
          )}
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议不超过50MB</Tip>
          <Button size="sm" onClick={handleVideoSelect(typeKey)}>
            选择视频
          </Button>
        </div>
        {!!coverUrl && renderPosSetting(typeKey)}
      </Collapse>
    );
  };

  const renderEnterType = () => {
    return (
      <Collapse title="进入方式设置">
        <Dropdown
          selected={state.enterMethod}
          items={[
            { id: EnterCoverEnter.auto, name: '自动进入' },
            { id: EnterCoverEnter.click, name: '点击进入' },
          ]}
          onChange={(enterKey) => {
            setState((draft) => {
              draft.enterMethod = enterKey;
            });
          }}
        />
        {state.enterMethod === EnterCoverEnter.auto && (
          <>
            <Slider
              min={3}
              max={10}
              className="mt-20"
              value={state.enterDuration}
              valToString={(val) => `${val}秒`}
              onChange={(val) =>
                setState((draft) => {
                  draft.enterDuration = val;
                })
              }
            >
              <Slider.Header title="停留时间：" />
              <Slider.Bar />
              <Slider.Footer />
            </Slider>
            <div className="mt-20 flex items-center justify-between">
              <div>显示倒计时：</div>
              <ToggleButton
                isOn={!!state.showCountDown}
                onToggle={(bool) => {
                  setState((draft) => {
                    draft.showCountDown = toFlag(bool);
                  });
                }}
              />
            </div>
          </>
        )}
      </Collapse>
    );
  };

  const renderVideoToggles = () => {
    return (
      <Collapse title="播放设置">
        <FeatureToggle
          title="自动播放："
          isOn={!!state.autoPlay}
          onToggle={(bool) => {
            setState((draft) => {
              draft.autoPlay = toFlag(bool);
            });
          }}
        />
        <FeatureToggle
          title="循环播放："
          isOn={!!state.loop}
          onToggle={(bool) => {
            setState((draft) => {
              draft.loop = toFlag(bool);
            });
          }}
        />
      </Collapse>
    );
  };

  const renderBgSetting = () => {
    return (
      <Collapse title="背景设置">
        <ColorPicker
          className="mt-10"
          colorString={state.bgColor}
          onChange={(str) =>
            setState((draft) => {
              draft.bgColor = str;
            })
          }
        >
          <ColorPicker.Header>
            <div>背景颜色：</div>
            <ColorPicker.Reset defaultColor={defaultState.bgColor!} />
          </ColorPicker.Header>
          <ColorPicker.Bar />
        </ColorPicker>
        <div className="mt-20">背景图片：</div>
        <ImageContainer
          className="mt-10 h-115"
          placeholder="图片将会平铺进行显示"
          onDeleteClick={() =>
            setState((draft) => {
              draft.bgImageId = defaultState.bgImageId;
              draft.bgImageUrl = defaultState.bgImageUrl;
            })
          }
        >
          {!!state.bgImageUrl && (
            <Image
              src={state.bgImageUrl}
              type={ImageType.media}
              className="max-h-full max-w-full"
            />
          )}
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>图片体积建议小于100KB</Tip>
          <Button
            size="sm"
            onClick={async () => {
              const { libItems } = await selectLibImages();
              if (!libItems) return;
              setState((draft) => {
                draft.bgImageId = libItems[0].id;
                draft.bgImageUrl = libItems[0].url;
              });
            }}
          >
            选择图片
          </Button>
        </div>
      </Collapse>
    );
  };

  return (
    <PanelBase
      isChanged={isChanged}
      onConfirm={handleConfirm}
      title={<Title>开场封面</Title>}
    >
      <Collapse title="开场类型">
        <Dropdown
          selected={state.type}
          items={[
            { id: EnterCoverType.image, name: '图片开场' },
            { id: EnterCoverType.video, name: '视频开场' },
          ]}
          onChange={handleTypeChange}
        />
      </Collapse>
      {state.type === EnterCoverType.image && (
        <>
          {renderCoverSetting({
            title: '桌面端封面图',
            size: '1920x1080',
            typeKey: 'pc',
          })}
          {renderCoverSetting({
            title: '移动端封面图',
            size: '1080x1920',
            typeKey: 'mobile',
          })}
        </>
      )}
      {state.type === EnterCoverType.video && (
        <>
          {renderVideoSetting({
            title: '桌面端封面视频',
            typeKey: 'pc',
          })}
          {renderVideoSetting({
            title: '移动端封面视频',
            typeKey: 'mobile',
          })}
        </>
      )}
      {hasCover && state.type === EnterCoverType.image && renderEnterType()}
      {hasCover && state.type === EnterCoverType.video && renderVideoToggles()}
      {hasCover && renderBgSetting()}
    </PanelBase>
  );
}

export default EnterCoverBase;
